import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function MissionVisionSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="mission-vision" className="py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D5E3F]/10 rounded-full mb-6">
            <span className="w-2 h-2 rounded-full bg-[#2D5E3F] inline-block"></span>
            <span className="text-[#2D5E3F] text-xs font-semibold tracking-widest uppercase">Our Purpose</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-5xl text-[#1C1C1C] font-semibold leading-tight max-w-3xl mx-auto">
            Guided by Purpose,<br />
            <em className="italic font-normal text-[#2D5E3F]">Driven by Values</em>
          </h2>
        </div>

        {/* Mission + Vision cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Mission */}
          <div className="relative rounded-2xl overflow-hidden group">
            <img
              src="https://readdy.ai/api/search-image?query=premium%20lumber%20yard%20with%20neatly%20stacked%20high%20quality%20wood%20planks%20in%20warm%20golden%20morning%20sunlight%2C%20organized%20timber%20storage%20facility%2C%20rich%20brown%20and%20amber%20wood%20tones%2C%20clean%20industrial%20setting%2C%20professional%20commercial%20photography%2C%20South%20American%20pine%20and%20eucalyptus%20timber&width=700&height=500&seq=mission-wood-01&orientation=landscape"
              alt="Our Mission"
              className="w-full h-64 object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C3A2C]/90 via-[#1C3A2C]/40 to-transparent"></div>
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C68B3C] rounded-full mb-4 w-fit">
                <i className="ri-focus-3-line text-white text-sm"></i>
                <span className="text-white text-xs font-semibold tracking-wider uppercase">Mission</span>
              </div>
              <p className="text-white text-sm leading-relaxed font-light">
                To facilitate the U.S. market&apos;s access to quality forest products from South America 
                and the world, offering transparent, efficient, and responsible brokerage services 
                that create value for both producers and buyers.
              </p>
            </div>
          </div>

          {/* Vision */}
          <div className="relative rounded-2xl overflow-hidden group">
            <img
              src="https://readdy.ai/api/search-image?query=panoramic%20view%20of%20sustainably%20managed%20forest%20plantation%20in%20Chile%2C%20rows%20of%20trees%20extending%20to%20horizon%2C%20early%20morning%20mist%2C%20vibrant%20green%20canopy%2C%20aerial%20perspective%20showing%20scale%20and%20order%20of%20responsible%20forestry%20operation%2C%20professional%20landscape%20photography%2C%20warm%20sunlight%20rays&width=700&height=500&seq=vision-forest-02&orientation=landscape"
              alt="Our Vision"
              className="w-full h-64 object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C3A2C]/90 via-[#1C3A2C]/40 to-transparent"></div>
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2D5E3F] rounded-full mb-4 w-fit">
                <i className="ri-eye-line text-white text-sm"></i>
                <span className="text-white text-xs font-semibold tracking-wider uppercase">Vision</span>
              </div>
              <p className="text-white text-sm leading-relaxed font-light">
                To be leaders in forest products brokerage between South America, the world, and 
                the United States — recognized for our service excellence, innovation in logistics 
                solutions, and unwavering commitment to sustainability.
              </p>
            </div>
          </div>
        </div>

        {/* Values strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: 'ri-hand-heart-line', title: 'Transparency', desc: 'Open, honest dealings at every step of the brokerage process.' },
            { icon: 'ri-recycle-line', title: 'Sustainability', desc: 'Committed to responsible sourcing that respects forests and communities.' },
            { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'Pioneering smarter logistics and supply chain solutions.' },
            { icon: 'ri-trophy-line', title: 'Excellence', desc: 'Delivering premium quality products with consistent reliability.' },
          ].map((val) => (
            <div key={val.title} className="p-6 rounded-2xl bg-[#F7F3EC] text-center hover:bg-[#2D5E3F] group transition-all duration-300 cursor-default">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2D5E3F]/10 group-hover:bg-white/20 mx-auto mb-4">
                <i className={`${val.icon} text-[#2D5E3F] group-hover:text-white text-xl`}></i>
              </div>
              <h4 className="font-semibold text-[#1C1C1C] group-hover:text-white text-sm mb-2">{val.title}</h4>
              <p className="text-stone-500 group-hover:text-white/80 text-xs leading-relaxed">{val.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
